/**
 * App-wide state for the shell: locale + translator + direction, UI preferences, signed-in identity,
 * and the sign-out / workspace-switch flows that purge device data (QM-D19-003, QM-D02-004).
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { createTranslator, dirFor, type Locale, type Translate } from '@qm/i18n';
import { DEFAULT_UI_PREFERENCES, type UiPreferences } from '@qm/contracts/shell';
import type { Me } from '@qm/contracts/identity';
import type { LocalePreferences } from '@qm/contracts/localization';
import { DEFAULT_LOCALE_PREFERENCES } from '@qm/contracts/localization';
import { bff, BffError, bffJson } from './bff';
import { browserDeviceApis, clearDeviceData } from './device';
import { applyKeepSignedIn } from './keep-signed-in';
import { LOCALE_STORAGE_KEY } from './prefs';
import { resetResourceCache } from './resource';
import { useTextOverrides } from './ui-text-hook';

export interface AppState {
  locale: Locale;
  dir: 'ltr' | 'rtl';
  t: Translate;
  localePrefs: LocalePreferences;
  ui: UiPreferences;
  me: Me | null;
  setLocale(l: Locale): Promise<void>;
  setLocalePrefs(p: Partial<LocalePreferences>): Promise<void>;
  setUi(p: Partial<UiPreferences>): Promise<void>;
  setMe(me: Me | null): void;
  /** apply server state without writing it back */
  hydrate(s: { me: Me; ui: UiPreferences; localePrefs: LocalePreferences }): void;
  signOut(): Promise<void>;
  switchWorkspace(tenantId: string): Promise<void>;
}

const Ctx = createContext<AppState | null>(null);

export interface AppProviderProps {
  children: ReactNode;
  initialLocale?: Locale;
  initialUi?: UiPreferences;
  initialMe?: Me | null;
  initialLocalePrefs?: LocalePreferences;
  /** replaced in tests */
  navigate?: (to: string) => void;
}

export function AppProvider({ children, initialLocale = 'en', initialUi = DEFAULT_UI_PREFERENCES, initialMe = null, initialLocalePrefs, navigate }: AppProviderProps) {
  const [localePrefs, setLp] = useState<LocalePreferences>(initialLocalePrefs ?? { ...DEFAULT_LOCALE_PREFERENCES, locale: initialLocale });
  const [ui, setUiState] = useState<UiPreferences>(initialUi);
  const [me, setMe] = useState<Me | null>(initialMe);
  const locale = localePrefs.locale;
  // published staff wording (Portal text CMS): loaded in the background, the translator is rebuilt when it arrives
  const textVersion = useTextOverrides(locale);
  const go = useCallback((to: string) => (navigate ? navigate(to) : window.location.assign(to)), [navigate]);

  const setLocalePrefs = useCallback(async (p: Partial<LocalePreferences>) => {
    setLp((cur) => ({ ...cur, ...p }));
    try { if (p.locale) localStorage.setItem(LOCALE_STORAGE_KEY, p.locale); } catch { /* storage blocked */ }
    if (me) await bffJson('/v1/localization/preferences', 'PUT', p).catch(() => undefined);
  }, [me]);

  const setUi = useCallback(async (p: Partial<UiPreferences>) => {
    setUiState((cur) => ({ ...cur, ...p }));
    if (me) await bffJson('/v1/shell/preferences', 'PUT', p).catch(() => undefined);
  }, [me]);

  const signOut = useCallback(async () => {
    await bffJson('/v1/identity/logout', 'POST').catch(() => undefined);
    await clearDeviceData(browserDeviceApis(resetResourceCache), 'sign_out');
    setMe(null);
    go('/signin');
  }, [go]);

  const switchWorkspace = useCallback(async (tenantId: string) => {
    await bffJson('/v1/identity/session/tenant', 'POST', { tenantId });
    await clearDeviceData(browserDeviceApis(resetResourceCache), 'switch_workspace');
    // full reload: no component state of the previous workspace survives
    go('/');
  }, [go]);

  const value = useMemo<AppState>(() => ({
    locale, dir: dirFor(locale), t: createTranslator(locale), localePrefs, ui, me,
    setLocale: (l) => setLocalePrefs({ locale: l }), setLocalePrefs, setUi, setMe, signOut, switchWorkspace,
    hydrate: (x) => { setMe(x.me); setUiState(x.ui); setLp(x.localePrefs); },
  }), [locale, textVersion, localePrefs, ui, me, setLocalePrefs, setUi, signOut, switchWorkspace]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useOptionalApp(): AppState | null { return useContext(Ctx); }

export function useApp(): AppState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useApp outside AppProvider');
  return v;
}

/**
 * Load the signed-in identity and server preferences once (called by the shell). `onUnauthorized` runs when
 * the API says there is no usable session (401), not on network or server errors.
 */
export async function loadSession(opts: { onUnauthorized?: () => void } = {}): Promise<{ me: Me; ui: UiPreferences; localePrefs: LocalePreferences } | null> {
  let me: Me | null;
  try {
    me = await bff<Me>('/v1/identity/me');
  } catch (err) {
    // A 401 that carries x-qm-session-state already navigates from the cross-tab guard (after the purge).
    // Calling onUnauthorized as well aborts that navigation.
    if (err instanceof BffError && err.status === 401 && !err.sessionEnded) opts.onUnauthorized?.();
    return null;
  }
  // null: anonymous 204 from the session probe (no cookie). Same outcome as a 401, without throwing.
  if (!me) { opts.onUnauthorized?.(); return null; }
  me = await applyKeepSignedIn(me);
  try {
    const [cfg, lp] = await Promise.all([
      bff<{ preferences: UiPreferences }>('/v1/shell/config').catch(() => ({ preferences: DEFAULT_UI_PREFERENCES })),
      me.tenantId ? bff<LocalePreferences>('/v1/localization/preferences').catch(() => DEFAULT_LOCALE_PREFERENCES) : Promise.resolve(DEFAULT_LOCALE_PREFERENCES),
    ]);
    return { me, ui: cfg.preferences, localePrefs: lp };
  } catch {
    return null;
  }
}
